import { useStore, useTranscription } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  buildSpeakersFromMappings,
  extractSpeakerLabelsFromTranscriptElements,
  fetchDatoSpeakers,
  fetchSpeakerVoiceMappings,
  hasSpeakerData,
} from '@src/utility/speakerUtils';
import { LogManager } from '@src/utility/Logger';
import { parseClipThemeTitles } from '@src/utility/story';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import AudienceFilterComponent from '../../common/AudienceFilterComponent';
import GoalFilterComponent from '../../common/GoalFilterComponent';
import Modal from '../../common/Modal';
import SpeakerFilterComponent from '../../common/SpeakerFilterComponent';
import { VideoTemplateFilterComponent } from '../../common/VideoTemplateFilterComponent';
import { VideoTemplatesProvider } from '../../sidepanel/VideoTemplateSettings/VideoTemplatesContext';

import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { AudienceOption } from '@src/components/common/AudienceSelector';
import type { GoalOption } from '@src/components/common/GoalSelector';
import type { ReelProducerConfig } from './types';

const logger = LogManager.getLogger('ReelProducerConfigModal');

const DURATION_PRESETS = [
  { label: '0:30', seconds: 30 },
  { label: '1:00', seconds: 60 },
  { label: '1:30', seconds: 90 },
  { label: '2:00', seconds: 120 },
  { label: '3:00', seconds: 180 },
  { label: '5:00', seconds: 300 },
] as const;

const DEFAULT_DURATION = 120;

type Props = {
  onSubmit: (config: ReelProducerConfig) => void;
  onClose: () => void;
};

export default function ReelProducerConfigModal({ onSubmit, onClose }: Props) {
  const videoCreator = useVideoCreatorStore();
  const transcriptionStore = useTranscription();
  const { userIdentityStore } = useStore();
  const flags = useFlagsCombination(
    userIdentityStore.currentIdentity?.role as any,
  );

  const [description, setDescription] = useState('');
  const [targetDuration, setTargetDuration] = useState(DEFAULT_DURATION);
  const [isCustomDuration, setIsCustomDuration] = useState(false);
  const [customMinutes, setCustomMinutes] = useState('2');
  const [customSeconds, setCustomSeconds] = useState('00');

  // Speaker state
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [selectedSpeakers, setSelectedSpeakers] = useState<Speaker[]>([]);
  const [isLoadingSpeakers, setIsLoadingSpeakers] = useState(false);
  const [useSpeakerFilter, setUseSpeakerFilter] = useState(false);

  // Goal state
  const [selectedGoal, setSelectedGoal] = useState<GoalOption | null>(null);
  const [useGoalFilter, setUseGoalFilter] = useState(false);

  // Audience state
  const [selectedAudiences, setSelectedAudiences] = useState<AudienceOption[]>(
    [],
  );
  const [useAudienceFilter, setUseAudienceFilter] = useState(false);

  // Video template state
  const [selectedVideoTemplateId, setSelectedVideoTemplateId] = useState<
    string | undefined
  >(undefined);
  const [useVideoTemplateFilter, setUseVideoTemplateFilter] = useState(false);

  // Topics from story
  const topics = useMemo(
    () => parseClipThemeTitles(videoCreator.story?.clipThemesJson),
    [videoCreator.story?.clipThemesJson],
  );
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const topicDropdownRef = useRef<HTMLDivElement>(null);
  const topicButtonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Position dropdown relative to the Wrapper (rendered at Wrapper level to escape ScrollArea clip)
  useLayoutEffect(() => {
    if (!showTopicDropdown || !topicButtonRef.current || !wrapperRef.current) return;
    const btnRect = topicButtonRef.current.getBoundingClientRect();
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'absolute',
      top: btnRect.bottom - wrapperRect.top + 6,
      right: wrapperRect.right - btnRect.right,
    });
  }, [showTopicDropdown]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showTopicDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (
        topicDropdownRef.current && !topicDropdownRef.current.contains(e.target as Node) &&
        topicButtonRef.current && !topicButtonRef.current.contains(e.target as Node)
      ) {
        setShowTopicDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showTopicDropdown]);

  const handleTopicToggle = useCallback((topic: string) => {
    setSelectedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topic)) {
        next.delete(topic);
      } else {
        next.add(topic);
      }
      return next;
    });
  }, []);

  const handleApplyTopics = useCallback(() => {
    if (selectedTopics.size === 0) return;
    const topicList = Array.from(selectedTopics).join(', ');
    const prefix = `Focus on topics: ${topicList}`;
    // Prepend to existing description, preserving any user text
    const existing = description.replace(/^Focus on topics:.*\n?/, '').trim();
    setDescription(existing ? `${prefix}\n${existing}` : prefix);
    setShowTopicDropdown(false);
  }, [selectedTopics, description]);

  const goals = videoCreator.organization?.branding?.goals || [];
  const audiences = videoCreator.organization?.branding?.audiences || [];

  const showSpeakerSelector = (() => {
    if (!flags.enableTranscriptEditor) return false;
    const transcriptElements =
      transcriptionStore.getFinalTranscriptionElements();
    return hasSpeakerData(transcriptElements);
  })();

  // Load speakers
  useEffect(() => {
    if (!showSpeakerSelector || !videoCreator.story?.id) return;

    let cancelled = false;
    const loadSpeakers = async () => {
      setIsLoadingSpeakers(true);
      try {
        const transcriptElements =
          transcriptionStore.getFinalTranscriptionElements();
        const speakerLabels =
          extractSpeakerLabelsFromTranscriptElements(transcriptElements);

        if (speakerLabels.length === 0) {
          if (!cancelled) setIsLoadingSpeakers(false);
          return;
        }

        const [speakerMappings, datoSpeakers] = await Promise.all([
          fetchSpeakerVoiceMappings(videoCreator.story!.id),
          fetchDatoSpeakers(
            videoCreator.story!.id,
            videoCreator.datoClientStore,
          ),
        ]);

        if (cancelled) return;
        const speakerList = buildSpeakersFromMappings(
          speakerLabels,
          speakerMappings,
          datoSpeakers,
        );

        setSpeakers(speakerList);
        setSelectedSpeakers(speakerList);
      } catch (error) {
        logger.error('Error loading speaker data:', error);
      } finally {
        if (!cancelled) setIsLoadingSpeakers(false);
      }
    };

    loadSpeakers();
    return () => {
      cancelled = true;
    };
  }, [videoCreator.story?.id, showSpeakerSelector]);

  const handleSubmit = () => {
    if (!description.trim()) return;
    // Clamp seconds to 59 in case the user typed > 59 and clicked Generate
    // without blurring the seconds input (blur handler clamps but may not fire).
    const clampedDuration = isCustomDuration
      ? (parseInt(customMinutes, 10) || 0) * 60 + Math.min(parseInt(customSeconds, 10) || 0, 59)
      : targetDuration;
    onSubmit({
      description,
      targetDuration: clampedDuration,
      allSpeakers: speakers,
      selectedSpeakers: useSpeakerFilter ? selectedSpeakers : [],
      selectedGoal: useGoalFilter ? selectedGoal : null,
      selectedAudiences: useAudienceFilter ? selectedAudiences : [],
      videoTemplateId: useVideoTemplateFilter
        ? (selectedVideoTemplateId ?? null)
        : null,
    });
  };

  const handlePresetClick = (seconds: number) => {
    setTargetDuration(seconds);
    setIsCustomDuration(false);
  };

  const handleCustomClick = () => {
    setIsCustomDuration(true);
  };

  const updateCustomDuration = (mins: string, secs: string) => {
    const m = parseInt(mins, 10) || 0;
    const s = parseInt(secs, 10) || 0;
    setTargetDuration(m * 60 + s);
  };

  const handleCustomMinutesChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 2);
    setCustomMinutes(cleaned);
    updateCustomDuration(cleaned, customSeconds);
  };

  const handleCustomSecondsChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 2);
    setCustomSeconds(cleaned);
    updateCustomDuration(customMinutes, cleaned);
  };

  const handleCustomSecondsBlur = () => {
    const num = Math.min(parseInt(customSeconds, 10) || 0, 59);
    setCustomSeconds(num.toString().padStart(2, '0'));
    updateCustomDuration(customMinutes, num.toString());
  };

  return (
    <Modal
      isOpen
      closeModal={onClose}
      blurBackground
      contentStyle={{
        marginTop: '70px',
        marginBottom: '70px',
      }}
    >
      <Wrapper ref={wrapperRef} data-testid="reel-producer-config-modal">
        <ScrollArea>
          <Content>
            <TopHeading>Produce a Reel</TopHeading>

            <Section>
              <DescriptionLabelRow>
                <Label>
                  Describe your reel <RequiredMark>*</RequiredMark>
                </Label>
                {topics.length > 0 && (
                  <TopicButtonWrapper>
                    <TopicButton
                      ref={topicButtonRef}
                      onClick={() => setShowTopicDropdown(prev => !prev)}
                      data-testid="reel-focus-topics-button"
                    >
                      Focus on topics
                    </TopicButton>
                  </TopicButtonWrapper>
                )}
              </DescriptionLabelRow>
              <DescriptionTextarea
                placeholder="What should this reel be about? (e.g., 'A highlight reel showcasing key moments from the interview about leadership')"
                value={description}
                onChange={e => setDescription(e.target.value)}
                data-testid="reel-description-input"
              />
            </Section>

            <Section>
              <Label>Target duration</Label>
              <DurationButtonRow>
                {DURATION_PRESETS.map(preset => (
                  <DurationButton
                    key={preset.seconds}
                    selected={!isCustomDuration && targetDuration === preset.seconds}
                    onClick={() => handlePresetClick(preset.seconds)}
                    data-testid={`reel-duration-${preset.seconds}`}
                  >
                    {preset.label}
                  </DurationButton>
                ))}
                <DurationButton
                  selected={isCustomDuration}
                  onClick={handleCustomClick}
                  data-testid="reel-duration-custom"
                >
                  Custom
                </DurationButton>
                {isCustomDuration && (
                  <CustomTimeInput>
                    <TimeField
                      type="text"
                      inputMode="numeric"
                      value={customMinutes}
                      onChange={e => handleCustomMinutesChange(e.target.value)}
                      autoFocus
                      data-testid="reel-duration-custom-minutes"
                    />
                    <TimeSeparator>:</TimeSeparator>
                    <TimeField
                      type="text"
                      inputMode="numeric"
                      value={customSeconds}
                      onChange={e => handleCustomSecondsChange(e.target.value)}
                      onBlur={handleCustomSecondsBlur}
                      data-testid="reel-duration-custom-seconds"
                    />
                  </CustomTimeInput>
                )}
              </DurationButtonRow>
            </Section>

            <CompactSections>
              {goals.length > 0 && (
                <GoalFilterComponent
                  align="left"
                  goals={goals}
                  selectedGoal={selectedGoal}
                  setSelectedGoal={setSelectedGoal}
                  useGoalFilter={useGoalFilter}
                  setUseGoalFilter={setUseGoalFilter}
                  allowNone
                />
              )}

              {audiences.length > 0 && (
                <AudienceFilterComponent
                  align="left"
                  audiences={audiences}
                  selectedAudiences={selectedAudiences}
                  setSelectedAudiences={setSelectedAudiences}
                  useAudienceFilter={useAudienceFilter}
                  setUseAudienceFilter={setUseAudienceFilter}
                />
              )}

              {showSpeakerSelector && (
                <SpeakerFilterComponent
                  align="left"
                  speakers={speakers}
                  selectedSpeakers={selectedSpeakers}
                  setSelectedSpeakers={setSelectedSpeakers}
                  useSpeakerFilter={useSpeakerFilter}
                  setUseSpeakerFilter={val =>
                    setUseSpeakerFilter(
                      typeof val === 'function' ? val(useSpeakerFilter) : val,
                    )
                  }
                  isLoadingSpeakers={isLoadingSpeakers}
                  title="Filter reel by speaker"
                />
              )}

              {flags.enableClipProducerVideoTemplateSelection && (
                <VideoTemplatesProvider
                  videoCreator={videoCreator}
                  editorStore={videoCreator.rootStore.editorStore}
                >
                  <VideoTemplateFilterComponent
                    useVideoTemplateFilter={useVideoTemplateFilter}
                    setUseVideoTemplateFilter={setUseVideoTemplateFilter}
                    selectedVideoTemplateId={selectedVideoTemplateId}
                    setSelectedVideoTemplateId={setSelectedVideoTemplateId}
                    align="left"
                  />
                </VideoTemplatesProvider>
              )}
            </CompactSections>
          </Content>
        </ScrollArea>

        {showTopicDropdown && (
          <TopicDropdown ref={topicDropdownRef} style={dropdownStyle}>
            <TopicDropdownHeader>Select topics</TopicDropdownHeader>
            <TopicList>
              {topics.map(topic => (
                <TopicItem
                  key={topic}
                  $selected={selectedTopics.has(topic)}
                  onClick={() => handleTopicToggle(topic)}
                >
                  <TopicCheckbox $checked={selectedTopics.has(topic)}>
                    {selectedTopics.has(topic) ? '✓' : ''}
                  </TopicCheckbox>
                  {topic}
                </TopicItem>
              ))}
            </TopicList>
            <TopicApplyButton
              onClick={handleApplyTopics}
              disabled={selectedTopics.size === 0}
            >
              Apply {selectedTopics.size > 0 ? `(${selectedTopics.size})` : ''}
            </TopicApplyButton>
          </TopicDropdown>
        )}

        <Footer>
          <GenerateButton
            onClick={handleSubmit}
            disabled={!description.trim()}
            data-testid="reel-generate-outline-button"
          >
            Generate Outline
          </GenerateButton>
        </Footer>
      </Wrapper>
    </Modal>
  );
}

const Wrapper = styled.div`
  border-radius: 10px;
  background-color: var(--background);
  width: 100%;
  max-width: 942px;
  min-width: 825px;
  border: 1px solid var(--border);
  height: 680px;
  max-height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  min-height: 0;
`;

const Content = styled.div`
  max-width: 580px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  padding: 0 24px 40px;
`;

const TopHeading = styled.h2`
  font-weight: 700;
  font-size: 32px;
  line-height: 38.73px;
  text-align: center;
  color: var(--foreground);
  margin: 0;
  padding-top: 40px;
  padding-bottom: 24px;
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  width: 100%;
  color: var(--foreground);
  font-size: 12px;
  font-weight: 500;
  line-height: 150%;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: var(--destructive);
  margin-left: 2px;
`;

const DescriptionLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const TopicButtonWrapper = styled.div`
  position: relative;
`;

const TopicButton = styled.button`
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #6ba3d6;
  background: transparent;
  color: #6ba3d6;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
  white-space: nowrap;

  &:hover {
    background: #6ba3d6;
    color: var(--background);
  }
`;

const TopicDropdown = styled.div`
  z-index: 100001;
  min-width: 240px;
  max-width: 320px;
  max-height: 240px;
  background: var(--surface, #1a1a1a);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
`;

const TopicDropdownHeader = styled.div`
  padding: 10px 12px 6px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TopicList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 0;
`;

const TopicItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: ${p => (p.$selected ? 'var(--foreground)' : 'var(--text-secondary)')};
  cursor: pointer;
  transition: background 100ms ease;

  &:hover {
    background: var(--sidebar-hover, rgba(255, 255, 255, 0.06));
  }
`;

const TopicCheckbox = styled.span<{ $checked: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1.5px solid ${p => (p.$checked ? 'var(--primary)' : 'var(--text-muted)')};
  background: ${p => (p.$checked ? 'var(--primary)' : 'transparent')};
  color: var(--background);
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TopicApplyButton = styled.button<{ disabled: boolean }>`
  margin: 4px 8px 8px;
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: var(--primary);
  color: var(--background);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  transition: opacity 150ms ease;

  &:hover {
    ${p => !p.disabled && 'opacity: 0.9;'}
  }
`;

const DescriptionTextarea = styled.textarea`
  box-sizing: border-box;
  display: flex;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  border: 1px solid var(--border);
  font-family: 'Inter', sans-serif;
  text-align: left;
  font-size: 12px;
  line-height: 150%;
  background-color: transparent;
  color: var(--text-secondary);
  width: 100%;
  resize: none;
  height: 90px;
  outline: none;
  transition: border-color 150ms ease;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    border-color: var(--accent);
  }
`;

const DurationButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DurationButton = styled.button<{ selected: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid ${props => (props.selected ? 'var(--accent)' : 'var(--border)')};
  background: ${props => (props.selected ? 'var(--accent)' : 'transparent')};
  color: ${props => (props.selected ? 'var(--background)' : 'var(--text-secondary)')};
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 150%;
  cursor: pointer;
  transition: all 150ms ease;

  &:hover {
    border-color: var(--accent);
  }
`;

const CustomTimeInput = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid var(--accent);
  background: transparent;
`;

const TimeField = styled.input`
  width: 24px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  outline: none;
  padding: 4px 0;
`;

const TimeSeparator = styled.span`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
`;

const CompactSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const Footer = styled.div`
  padding: 24px 0 16px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -40px;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      var(--background) 100%
    );
    pointer-events: none;
  }
`;

const GenerateButton = styled.button<{ disabled: boolean }>`
  width: 240px;
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  box-sizing: border-box;
  font-family: 'Inter', sans-serif;

  color: var(--background);
  font-size: 14px;
  font-weight: 700;
  line-height: normal;
  cursor: pointer;

  ${props =>
    props.disabled &&
    `
    cursor: not-allowed;
    color: var(--background);
    opacity: 0.6;
  `}
`;
